import { Formik } from 'formik';

import { invariant } from '@e-socna/utils';
import type { GenericStatusId } from '@e-socna/data-layer';

import type { MemosState } from '../MemoBeforeSubmit';

import { useGenericAdminSubmit } from './hooks/useSubmit.hook';
import { useInitialValuesFactory } from './hooks/useInitialValuesFactory.hook';
import { GenericAdminFormFields } from './GenericAdminFormFields';
import type { SubmitHandler } from './types/submit-handler.type';

export type { OpaqueGenericAdminFormValues as GenericAdminFormValues } from './types/generic-admin-form-values.type';

interface Props {
  readonly askMemoBeforeSubmit?: boolean;
  readonly clearMemos?: () => void;
  readonly memos?: MemosState;
  readonly onCancel: () => unknown;
  readonly onSubmit: SubmitHandler;
  readonly setMemos?: (
    setMemosFn: ((memos: MemosState) => MemosState) | MemosState,
  ) => void;
  readonly setMustBlockAction?: (mustBlock: boolean) => void;
  readonly statusId: GenericStatusId;
}

export const GenericAdminForm = ({
  askMemoBeforeSubmit = true,
  clearMemos,
  memos,
  onCancel: handleCancel,
  onSubmit: pHandleSubmit,
  setMemos,
  setMustBlockAction,
  statusId,
}: Readonly<Props>) => {
  const { initialValues } = useInitialValuesFactory({ statusId });

  const { formError, handleSubmit } = useGenericAdminSubmit({
    handleSubmit: pHandleSubmit,
  });

  invariant(
    !askMemoBeforeSubmit || !!(memos && setMemos && clearMemos),
    'GenericAdminForm: askMemoBeforeSubmit is true but memos, setMemos or clearMemos are not provided.',
  );

  invariant(
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    askMemoBeforeSubmit || !(memos || setMemos || clearMemos),
    'GenericAdminForm: askMemoBeforeSubmit is false but memos, setMemos or clearMemos are provided.',
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      <GenericAdminFormFields
        askMemoBeforeSubmit={askMemoBeforeSubmit}
        clearMemos={clearMemos}
        formError={formError}
        memos={memos}
        onCancel={handleCancel}
        setMemos={setMemos}
        setMustBlockAction={setMustBlockAction}
      />
    </Formik>
  );
};
